import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    adminLogin,
    adminMe,
    confirmBooking,
    declineBooking,
    deleteBooking,
    fetchAdminAnalytics,
    fetchAdminBookings,
    fetchAdminVouchers,
    getAdminToken,
    redeemVoucher,
    setAdminToken,
    undoVoucher,
    voidVoucher,
} from "../lib/api";
import { MONOGRAM_URL } from "../lib/data";
import { format12h } from "./BookPage";

export default function AdminPage() {
    const [authed, setAuthed] = useState(false);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        const token = getAdminToken();
        if (!token) {
            setChecking(false);
            return;
        }
        adminMe()
            .then(() => setAuthed(true))
            .catch(() => setAdminToken(""))
            .finally(() => setChecking(false));
    }, []);

    if (checking) {
        return (
            <div className="min-h-screen bg-[#0E0E0E] text-[#FAF8F4] flex items-center justify-center">
                <p className="overline text-[#FAF8F4]/60">Loading…</p>
            </div>
        );
    }

    if (!authed) {
        return <AdminLogin onAuthed={() => setAuthed(true)} />;
    }

    return <AdminDashboard onLogout={() => {
        setAdminToken("");
        setAuthed(false);
    }} />;
}

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------
function AdminLogin({ onAuthed }) {
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    async function onSubmit(e) {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await adminLogin(password);
            onAuthed();
        } catch (err) {
            setError(err.message || "Login failed");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div
            data-testid="admin-login"
            className="min-h-screen bg-[#0E0E0E] text-[#FAF8F4] flex items-center justify-center px-6"
        >
            {/* MOBILE — back arrow returns to home */}
            <Link
                to="/"
                data-testid="admin-login-back-mobile"
                aria-label="Back to site"
                className="md:hidden absolute top-5 left-5 inline-flex items-center justify-center w-12 h-12 text-[#FAF8F4]/70 hover:text-[#EB4A18] transition-colors"
            >
                <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-9 h-9"
                >
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
            </Link>
            {/* DESKTOP — home icon returns to home */}
            <Link
                to="/"
                data-testid="admin-login-home-desktop"
                aria-label="Back to site"
                className="hidden md:inline-flex absolute top-6 left-6 items-center gap-2 overline text-[#FAF8F4]/60 hover:text-[#EB4A18] transition-colors tracking-[0.28em]"
            >
                <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-5 h-5"
                >
                    <path d="M3 11l9-8 9 8" />
                    <path d="M5 10v10h14V10" />
                </svg>
                Home
            </Link>
            <motion.form
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
                onSubmit={onSubmit}
                className="w-full max-w-md"
            >
                <div className="flex items-center gap-3 mb-10">
                    <img
                        src={MONOGRAM_URL}
                        alt=""
                        aria-hidden="true"
                        className="h-10 w-auto"
                        style={{ filter: "invert(1)" }}
                    />
                    <div>
                        <p className="overline !text-[#FAF8F4]/50 text-[0.55rem]">
                            The Gaffer
                        </p>
                        <p className="font-display uppercase text-2xl tracking-tight">
                            Owner Dashboard
                        </p>
                    </div>
                </div>

                <label htmlFor="admin-pw" className="block">
                    <span className="overline !text-[#FAF8F4]/60">
                        Password
                    </span>
                    <input
                        id="admin-pw"
                        data-testid="admin-password-input"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoFocus
                        autoComplete="current-password"
                        className="mt-2 w-full bg-transparent border-b-2 border-[#FAF8F4]/30 py-3 text-2xl font-display tracking-tight focus:border-[#EB4A18] outline-none transition-colors"
                    />
                </label>

                {error && (
                    <p
                        data-testid="admin-login-error"
                        className="mt-4 text-sm text-[#EB4A18]"
                    >
                        {error}
                    </p>
                )}

                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="admin-login-submit"
                    className="mt-8 inline-flex items-center justify-center gap-3 bg-[#EB4A18] hover:bg-[#FAF8F4] hover:text-[#121212] text-[#FAF8F4] px-8 py-4 text-xs uppercase tracking-[0.22em] font-bold transition-colors duration-300 disabled:opacity-60"
                >
                    {submitting ? "…" : "Sign In"}
                    {!submitting && <span aria-hidden="true">→</span>}
                </button>

                <p className="mt-10 overline !text-[#FAF8F4]/30 text-[0.55rem]">
                    Internal only — bookings + service.
                </p>
            </motion.form>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
function AdminDashboard({ onLogout }) {
    const [view, setView] = useState("bookings"); // "bookings" | "analytics"
    const [statusFilter, setStatusFilter] = useState("pending");
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState("");
    const [error, setError] = useState("");

    async function load(silent = false) {
        if (!silent) setLoading(true);
        setError("");
        try {
            const data = await fetchAdminBookings(statusFilter);
            setBookings(data);
        } catch (err) {
            if (!silent) setError(err.message || "Failed to load");
        } finally {
            if (!silent) setLoading(false);
        }
    }

    useEffect(() => {
        load();
        // Poll every 8 seconds for real-time updates (covers customer
        // cancellations that come in via SMS while the owner is logged in).
        const id = setInterval(() => load(true), 8000);
        return () => clearInterval(id);
    }, [statusFilter]);

    async function onConfirm(id) {
        setBusyId(id);
        try {
            await confirmBooking(id);
            await load();
        } catch (err) {
            setError(err.message || "Failed to confirm");
        } finally {
            setBusyId("");
        }
    }

    async function onDecline(id) {
        setBusyId(id);
        try {
            await declineBooking(id);
            await load();
        } catch (err) {
            setError(err.message || "Failed to decline");
        } finally {
            setBusyId("");
        }
    }

    async function onDelete(id) {
        if (!window.confirm("Permanently delete this booking? This cannot be undone.")) {
            return;
        }
        setBusyId(id);
        try {
            await deleteBooking(id);
            await load();
        } catch (err) {
            setError(err.message || "Failed to delete");
        } finally {
            setBusyId("");
        }
    }

    const counts = bookings.length;

    return (
        <div
            data-testid="admin-dashboard"
            className="min-h-screen bg-[#0E0E0E] text-[#FAF8F4]"
        >
            {/* Topbar */}
            <header className="border-b border-[#FAF8F4]/10">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img
                            src={MONOGRAM_URL}
                            alt=""
                            aria-hidden="true"
                            className="h-8 w-auto"
                            style={{ filter: "invert(1)" }}
                        />
                        <div className="flex flex-col">
                            <span className="overline !text-[#FAF8F4]/50 text-[0.55rem]">
                                The Gaffer
                            </span>
                            <span className="font-display uppercase text-lg md:text-xl leading-none">
                                Owner Dashboard
                            </span>
                        </div>
                    </div>
                    <button
                        data-testid="admin-logout"
                        onClick={onLogout}
                        className="overline !text-[#FAF8F4]/70 hover:!text-[#EB4A18] transition-colors"
                    >
                        Sign out →
                    </button>
                </div>
            </header>

            <main className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-10 md:py-14">
                {/* Top-level section toggle */}
                <div className="flex gap-6 mb-8 border-b border-[#FAF8F4]/10">
                    {[
                        { v: "bookings", label: "Bookings" },
                        { v: "vouchers", label: "Vouchers" },
                        { v: "analytics", label: "Analytics" },
                    ].map((t) => {
                        const active = view === t.v;
                        return (
                            <button
                                key={t.v}
                                data-testid={`admin-view-${t.v}`}
                                onClick={() => setView(t.v)}
                                className={`pb-4 -mb-px text-xs uppercase tracking-[0.22em] font-bold border-b-2 transition-colors duration-200 ${
                                    active
                                        ? "border-[#EB4A18] text-[#FAF8F4]"
                                        : "border-transparent text-[#FAF8F4]/40 hover:text-[#FAF8F4]/80"
                                }`}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                {view === "bookings" && (
                    <>
                        {/* Heading */}
                        <div className="flex items-end justify-between flex-wrap gap-4">
                            <div>
                                <p className="overline !text-[#FAF8F4]/50">
                                    Bookings
                                </p>
                                <h1 className="font-display uppercase text-[clamp(2.2rem,4vw,3.5rem)] leading-[0.95]">
                                    Service<span className="text-[#EB4A18]">.</span>
                                </h1>
                            </div>
                            <p className="overline !text-[#FAF8F4]/60">
                                {counts} {counts === 1 ? "booking" : "bookings"}
                            </p>
                        </div>

                        {/* Tabs */}
                        <div className="mt-8 flex flex-wrap gap-2">
                            {[
                                { v: "pending", label: "Pending" },
                                { v: "confirmed", label: "Confirmed" },
                                { v: "declined", label: "Declined" },
                                { v: "cancelled", label: "Cancelled" },
                                { v: "all", label: "All" },
                            ].map((t) => {
                                const active = statusFilter === t.v;
                                return (
                                    <button
                                        key={t.v}
                                        data-testid={`admin-tab-${t.v}`}
                                        onClick={() => setStatusFilter(t.v)}
                                        className={`px-5 py-2.5 text-xs uppercase tracking-[0.22em] font-bold border transition-colors duration-200 ${
                                            active
                                                ? "border-[#EB4A18] bg-[#EB4A18] text-[#FAF8F4]"
                                                : "border-[#FAF8F4]/20 text-[#FAF8F4]/70 hover:border-[#FAF8F4]/60 hover:text-[#FAF8F4]"
                                        }`}
                                    >
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>

                        {error && (
                            <p
                                data-testid="admin-error"
                                className="mt-6 text-sm text-[#EB4A18]"
                            >
                                {error}
                            </p>
                        )}

                        {/* Bookings */}
                        <div className="mt-8 space-y-4">
                            {loading && (
                                <p className="overline !text-[#FAF8F4]/50">Loading…</p>
                            )}
                            {!loading && bookings.length === 0 && (
                                <div className="border border-dashed border-[#FAF8F4]/20 p-10 text-center">
                                    <p className="overline !text-[#FAF8F4]/60">
                                        No bookings here yet.
                                    </p>
                                </div>
                            )}
                            {!loading &&
                                bookings.map((b) => (
                                    <BookingRow
                                        key={b.id}
                                        b={b}
                                        busy={busyId === b.id}
                                        onConfirm={onConfirm}
                                        onDecline={onDecline}
                                        onDelete={onDelete}
                                    />
                                ))}
                        </div>
                    </>
                )}

                {view === "analytics" && <AnalyticsView />}
                {view === "vouchers" && <VouchersView />}
            </main>
        </div>
    );
}

function statusPill(status) {
    const cfg = {
        pending: { label: "Pending", bg: "#3A2A0E", fg: "#F2B438" },
        confirmed: { label: "Confirmed", bg: "#0E2A1A", fg: "#3FA862" },
        declined: { label: "Declined", bg: "#2A0E10", fg: "#EB4A18" },
        cancelled: { label: "Cancelled", bg: "#1A1A2A", fg: "#A89BFF" },
    }[status] || { label: status, bg: "#222", fg: "#fff" };
    return (
        <span
            className="inline-flex items-center px-3 py-1 text-[0.6rem] uppercase tracking-[0.28em] font-bold"
            style={{ backgroundColor: cfg.bg, color: cfg.fg }}
        >
            {cfg.label}
        </span>
    );
}

// Subtle helper line under the status pill — explains where/when this
// booking's status was changed. Returns null for "pending" so the line
// doesn't appear on un-actioned bookings.
function sourceLine(b) {
    if (b.status === "pending") return null;
    const sourceLabel = {
        dashboard: "via Dashboard",
        sms: "via SMS reply",
        customer: "by customer",
    }[b.confirmed_via] || "via Mobile";
    let when = "";
    if (b.actioned_at) {
        const diffMs = Date.now() - new Date(b.actioned_at).getTime();
        const mins = Math.round(diffMs / 60000);
        if (mins < 1) when = " · just now";
        else if (mins < 60) when = ` · ${mins} min ago`;
        else if (mins < 1440) when = ` · ${Math.round(mins / 60)} hr ago`;
        else when = ` · ${Math.round(mins / 1440)} d ago`;
    }
    return (
        <p className="mt-2 text-[0.6rem] uppercase tracking-[0.18em] text-[#FAF8F4]/35">
            {sourceLabel}
            {when}
        </p>
    );
}

function BookingRow({ b, busy, onConfirm, onDecline, onDelete }) {
    return (
        <motion.article
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
            data-testid={`admin-booking-${b.id}`}
            className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center bg-[#161616] border border-[#FAF8F4]/8 px-6 md:px-8 py-6"
        >
            <div className="lg:col-span-2">
                <p className="overline !text-[#FAF8F4]/45">Party</p>
                <p className="font-display text-4xl text-[#EB4A18] leading-none mt-1">
                    {b.party_size}
                </p>
            </div>
            <div className="lg:col-span-3">
                <p className="overline !text-[#FAF8F4]/45">When</p>
                <p className="font-display text-2xl md:text-3xl leading-tight mt-1">
                    {b.date}
                </p>
                <p className="font-display text-xl text-[#FAF8F4]/80">
                    @ {format12h(b.time)}
                </p>
            </div>
            <div className="lg:col-span-3">
                <p className="overline !text-[#FAF8F4]/45">Guest</p>
                <p className="font-display text-2xl leading-tight mt-1">
                    {b.name}
                </p>
                <a
                    href={`tel:${b.mobile}`}
                    className="block mt-1 text-sm text-[#FAF8F4]/70 hover:text-[#EB4A18]"
                >
                    {b.mobile}
                </a>
                {b.email && (
                    <a
                        href={`mailto:${b.email}`}
                        className="block text-xs text-[#FAF8F4]/50 hover:text-[#EB4A18] truncate"
                    >
                        {b.email}
                    </a>
                )}
            </div>
            <div className="lg:col-span-2">
                <p className="overline !text-[#FAF8F4]/45">Notes</p>
                <p className="text-sm text-[#FAF8F4]/80 mt-1">
                    {b.special_requests || "—"}
                </p>
                <div className="mt-3">
                    {statusPill(b.status)}
                    {sourceLine(b)}
                </div>
            </div>
            <div className="lg:col-span-2 flex flex-col gap-2">
                {b.status !== "confirmed" && b.status !== "cancelled" && (
                    <button
                        disabled={busy}
                        data-testid={`admin-confirm-${b.id}`}
                        onClick={() => onConfirm(b.id)}
                        className="inline-flex items-center justify-center gap-2 bg-[#EB4A18] hover:bg-[#FAF8F4] hover:text-[#121212] text-[#FAF8F4] px-5 py-3 text-[0.65rem] uppercase tracking-[0.22em] font-bold transition-colors duration-200 disabled:opacity-50"
                    >
                        Confirm
                        <span aria-hidden="true">✓</span>
                    </button>
                )}
                {b.status !== "declined" && b.status !== "cancelled" && (
                    <button
                        disabled={busy}
                        data-testid={`admin-decline-${b.id}`}
                        onClick={() => onDecline(b.id)}
                        className="inline-flex items-center justify-center gap-2 bg-transparent border border-[#FAF8F4]/30 hover:border-[#EB4A18] hover:text-[#EB4A18] text-[#FAF8F4]/80 px-5 py-3 text-[0.65rem] uppercase tracking-[0.22em] font-bold transition-colors duration-200 disabled:opacity-50"
                    >
                        Decline
                    </button>
                )}
                <button
                    disabled={busy}
                    data-testid={`admin-delete-${b.id}`}
                    onClick={() => onDelete(b.id)}
                    className="inline-flex items-center justify-center gap-2 bg-transparent text-[#FAF8F4]/40 hover:text-[#EB4A18] px-5 py-2 text-[0.6rem] uppercase tracking-[0.22em] font-bold transition-colors duration-200 disabled:opacity-50"
                    title="Permanently delete this booking"
                >
                    Delete
                    <span aria-hidden="true">×</span>
                </button>
            </div>
        </motion.article>
    );
}

// ---------------------------------------------------------------------------
// Analytics view — visits + CTA click counts
// ---------------------------------------------------------------------------
const CTA_LABELS = {
    order_pickup: "Order Pickup (Chewzie)",
    delivery: "Delivery (Uber Eats)",
    book_table: "Book a Table",
    phone_call: "Phone Call",
    directions: "Directions",
    facebook: "Facebook",
    see_menu: "See Menu",
};
const PAGE_LABELS = {
    "/": "Home",
    "/menu": "Menu",
    "/about": "About",
    "/book": "Book a Table",
};

function AnalyticsView() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    async function load() {
        setLoading(true);
        setErr("");
        try {
            const d = await fetchAdminAnalytics();
            setData(d);
        } catch (e) {
            setErr(e.message || "Failed to load analytics");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
        const id = setInterval(load, 15000); // refresh every 15s
        return () => clearInterval(id);
    }, []);

    if (loading && !data) {
        return (
            <p className="overline !text-[#FAF8F4]/50">Loading analytics…</p>
        );
    }
    if (err) {
        return (
            <p data-testid="analytics-error" className="text-sm text-[#EB4A18]">
                {err}
            </p>
        );
    }
    if (!data) return null;

    const visits = data.visits || {};
    const clicks7d = data.clicks?.last_7d || {};
    const clicks30d = data.clicks?.last_30d || {};
    const pages7d = data.top_pages_7d || {};
    // Conversion rate = visitors who clicked one of the three core revenue
    // CTAs (pickup / delivery / book) ÷ total visitors. Phone & directions
    // are excluded because not everyone needs them; including them inflates
    // the metric.
    const coreClicks7d =
        (clicks7d.order_pickup || 0) +
        (clicks7d.delivery || 0) +
        (clicks7d.book_table || 0);
    const totalVisits7d = visits.last_7d || 0;
    const ctaRate7d =
        totalVisits7d > 0
            ? Math.round((coreClicks7d / totalVisits7d) * 100)
            : 0;

    return (
        <div data-testid="analytics-view" className="space-y-10">
            {/* Heading */}
            <div>
                <p className="overline !text-[#FAF8F4]/50">Analytics</p>
                <h1 className="font-display uppercase text-[clamp(2.2rem,4vw,3.5rem)] leading-[0.95]">
                    Traffic<span className="text-[#EB4A18]">.</span>
                </h1>
                <p className="overline !text-[#FAF8F4]/40 text-[0.6rem] mt-2">
                    Refreshes every 15 seconds. Admin views are excluded.
                </p>
            </div>

            {/* Visit cards */}
            <section>
                <p className="overline !text-[#FAF8F4]/50 mb-4">Site visits</p>
                <div className="grid grid-cols-3 gap-3 md:gap-6">
                    <StatCard label="Today" value={visits.today || 0} />
                    <StatCard label="Last 7 days" value={visits.last_7d || 0} />
                    <StatCard
                        label="Last 30 days"
                        value={visits.last_30d || 0}
                        footerLabel="All time"
                        footerValue={visits.all_time || 0}
                    />
                </div>
            </section>

            {/* CTA conversion */}
            <section>
                <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
                    <p className="overline !text-[#FAF8F4]/50">CTA clicks</p>
                </div>
                <p className="text-sm text-[#FAF8F4]/60 mb-5 max-w-2xl">
                    Of all visitors in the last 7 days,{" "}
                    <span className="text-[#EB4A18] font-bold">
                        {ctaRate7d}%
                    </span>{" "}
                    clicked a key action button (Order, Delivery, or Book).
                </p>
                <div className="border border-[#FAF8F4]/10">
                    {/* Header row */}
                    <div className="grid grid-cols-[1.5fr_1fr_1fr] px-5 py-3 border-b border-[#FAF8F4]/10 bg-[#FAF8F4]/[0.02]">
                        <span className="overline !text-[#FAF8F4]/40 text-[0.55rem]">
                            Action
                        </span>
                        <span className="overline !text-[#FAF8F4]/40 text-[0.55rem] text-right">
                            Last 7 days
                        </span>
                        <span className="overline !text-[#FAF8F4]/40 text-[0.55rem] text-right">
                            Last 30 days
                        </span>
                    </div>
                    {/* Rows */}
                    <div className="divide-y divide-[#FAF8F4]/10">
                        {Object.keys(CTA_LABELS)
                            .map((key) => ({
                                key,
                                c7: clicks7d[key] || 0,
                                c30: clicks30d[key] || 0,
                            }))
                            .sort((a, b) => {
                                // Higher 7d count first; tie-break by 30d.
                                if (b.c7 !== a.c7) return b.c7 - a.c7;
                                return b.c30 - a.c30;
                            })
                            .map(({ key, c7, c30 }) => (
                                <div
                                    key={key}
                                    data-testid={`analytics-cta-${key}`}
                                    className="grid grid-cols-[1.5fr_1fr_1fr] px-5 py-4 items-center"
                                >
                                    <span className="text-sm text-[#FAF8F4]/90">
                                        {CTA_LABELS[key]}
                                    </span>
                                    <span className="font-display text-2xl text-right text-[#FAF8F4]">
                                        {c7}
                                    </span>
                                    <span className="font-display text-2xl text-right text-[#FAF8F4]/50">
                                        {c30}
                                    </span>
                                </div>
                            ))}
                    </div>
                </div>
            </section>

            {/* Top pages */}
            <section>
                <p className="overline !text-[#FAF8F4]/50 mb-4">
                    Top pages (last 7 days)
                </p>
                <div className="border border-[#FAF8F4]/10">
                    {/* Header */}
                    <div className="grid grid-cols-[1.5fr_1fr_1fr] px-5 py-3 border-b border-[#FAF8F4]/10 bg-[#FAF8F4]/[0.02]">
                        <span className="overline !text-[#FAF8F4]/40 text-[0.55rem]">
                            Page
                        </span>
                        <span className="overline !text-[#FAF8F4]/40 text-[0.55rem]">
                            Path
                        </span>
                        <span className="overline !text-[#FAF8F4]/40 text-[0.55rem] text-right">
                            Views
                        </span>
                    </div>
                    <div className="divide-y divide-[#FAF8F4]/10">
                        {Object.keys(pages7d).length === 0 && (
                            <p className="px-5 py-4 overline !text-[#FAF8F4]/40">
                                No views yet
                            </p>
                        )}
                        {Object.entries(pages7d)
                            .sort((a, b) => b[1] - a[1])
                            .map(([path, n]) => (
                                <div
                                    key={path}
                                    className="grid grid-cols-[1.5fr_1fr_1fr] px-5 py-3 items-center"
                                >
                                    <span className="text-sm text-[#FAF8F4]/90">
                                        {PAGE_LABELS[path] || path}
                                    </span>
                                    <span className="text-xs text-[#FAF8F4]/40 truncate">
                                        {path}
                                    </span>
                                    <span className="font-display text-xl text-right text-[#FAF8F4]">
                                        {n}
                                    </span>
                                </div>
                            ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

function StatCard({ label, value, footerLabel, footerValue }) {
    return (
        <div className="relative border border-[#FAF8F4]/10 px-4 md:px-6 py-5 md:py-7">
            <p className="overline !text-[#FAF8F4]/40 text-[0.55rem]">
                {label}
            </p>
            <p className="font-display text-3xl md:text-5xl text-[#FAF8F4] mt-2 leading-none">
                {value}
            </p>
            {footerLabel !== undefined && (
                <div className="absolute bottom-3 right-4 md:bottom-4 md:right-5 text-right">
                    <p className="overline !text-[#FAF8F4]/30 text-[0.5rem] leading-none">
                        {footerLabel}
                    </p>
                    <p className="font-display text-lg md:text-2xl text-[#FAF8F4]/50 leading-none mt-1">
                        {footerValue}
                    </p>
                </div>
            )}
        </div>
    );
}


// ---------------------------------------------------------------------------
// Vouchers view — list, redeem, void
// ---------------------------------------------------------------------------
function VouchersView() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [filter, setFilter] = useState("active"); // active | used | voided | all
    const [helpOpen, setHelpOpen] = useState(false);
    const [search, setSearch] = useState("");

    async function load() {
        setError("");
        try {
            const data = await fetchAdminVouchers();
            setItems(data);
        } catch (e) {
            setError(e.message || "Failed to load vouchers");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
        const id = setInterval(load, 15000);
        return () => clearInterval(id);
    }, []);

    const q = search.trim().toLowerCase();
    const filtered = items
        .filter((v) => (filter === "all" ? true : v.status === filter))
        .filter((v) => {
            if (!q) return true;
            return (
                (v.code || "").toLowerCase().includes(q) ||
                (v.buyer_name || "").toLowerCase().includes(q) ||
                (v.recipient_name || "").toLowerCase().includes(q) ||
                (v.buyer_email || "").toLowerCase().includes(q)
            );
        });
    const totals = items.reduce(
        (acc, v) => {
            acc.sold += v.amount_initial || 0;
            acc.redeemed += (v.amount_initial || 0) - (v.amount_remaining || 0);
            acc.outstanding += v.status === "active" ? v.amount_remaining || 0 : 0;
            return acc;
        },
        { sold: 0, redeemed: 0, outstanding: 0 }
    );

    return (
        <div data-testid="admin-vouchers-view" className="space-y-8">
            {/* Heading */}
            <div>
                <p className="overline !text-[#FAF8F4]/50">Vouchers</p>
                <div className="flex items-center gap-4">
                    <h1 className="font-display uppercase text-[clamp(2.2rem,4vw,3.5rem)] leading-[0.95]">
                        Store credit<span className="text-[#EB4A18]">.</span>
                    </h1>
                    <button
                        type="button"
                        data-testid="vouchers-help-btn"
                        onClick={() => setHelpOpen(true)}
                        aria-label="How to redeem a voucher"
                        title="How to redeem a voucher"
                        className="mt-2 inline-flex items-center justify-center w-8 h-8 rounded-full border border-[#FAF8F4]/25 text-[#FAF8F4]/50 hover:text-[#FAF8F4] hover:border-[#EB4A18] hover:bg-[#EB4A18]/10 text-sm font-bold transition-colors duration-200"
                    >
                        ?
                    </button>
                </div>
            </div>

            {helpOpen && <VoucherHelpModal onClose={() => setHelpOpen(false)} />}

            {/* Totals */}
            <div className="grid grid-cols-3 gap-3 md:gap-6">
                <StatCard label="Total sold" value={`$${totals.sold.toFixed(2)}`} />
                <StatCard
                    label="Redeemed"
                    value={`$${totals.redeemed.toFixed(2)}`}
                />
                <StatCard
                    label="Outstanding"
                    value={`$${totals.outstanding.toFixed(2)}`}
                />
            </div>

            {/* Filter tabs */}
            <div className="flex flex-wrap gap-2">
                {[
                    { v: "active", label: "Active" },
                    { v: "used", label: "Used" },
                    { v: "voided", label: "Voided" },
                    { v: "all", label: "All" },
                ].map((t) => {
                    const active = filter === t.v;
                    return (
                        <button
                            key={t.v}
                            data-testid={`vouchers-tab-${t.v}`}
                            onClick={() => setFilter(t.v)}
                            className={`px-4 py-2 text-xs uppercase tracking-[0.22em] font-bold border transition-colors duration-200 ${
                                active
                                    ? "border-[#EB4A18] bg-[#EB4A18] text-[#FAF8F4]"
                                    : "border-[#FAF8F4]/20 text-[#FAF8F4]/70 hover:border-[#FAF8F4]/60 hover:text-[#FAF8F4]"
                            }`}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {/* Search */}
            <div className="relative">
                <input
                    type="text"
                    data-testid="vouchers-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name or code (e.g. Sarah or GAF-9K2M)"
                    className="w-full md:max-w-lg pl-11 pr-4 py-3 bg-[#0B0A09] border border-[#FAF8F4]/15 focus:border-[#EB4A18] outline-none text-[#FAF8F4] text-sm placeholder:text-[#FAF8F4]/30"
                />
                <span
                    aria-hidden="true"
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-[#FAF8F4]/40"
                >
                    ⌕
                </span>
                {search && (
                    <button
                        type="button"
                        onClick={() => setSearch("")}
                        aria-label="Clear search"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#FAF8F4]/40 hover:text-[#EB4A18] w-6 h-6 flex items-center justify-center"
                    >
                        ×
                    </button>
                )}
            </div>

            {error && <p className="text-sm text-[#EB4A18]">{error}</p>}

            {/* List */}
            <div className="space-y-3">
                {loading && (
                    <p className="overline !text-[#FAF8F4]/50">Loading…</p>
                )}
                {!loading && filtered.length === 0 && (
                    <div className="border border-dashed border-[#FAF8F4]/20 p-10 text-center">
                        <p className="overline !text-[#FAF8F4]/60">
                            No vouchers here yet.
                        </p>
                    </div>
                )}
                {!loading &&
                    filtered.map((v) => (
                        <VoucherRow
                            key={v.code}
                            v={v}
                            highlight={q}
                            onChange={load}
                        />
                    ))}
            </div>
        </div>
    );
}

// Highlight matches of `q` inside `text` — wraps matched substrings in an
// orange <mark> so search hits pop in the voucher list.
function highlightMatch(text, q) {
    if (!text) return text;
    const s = String(text);
    if (!q) return s;
    const idx = s.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return s;
    return (
        <>
            {s.slice(0, idx)}
            <mark className="bg-[#EB4A18]/25 text-[#EB4A18] px-0.5 rounded-sm">
                {s.slice(idx, idx + q.length)}
            </mark>
            {s.slice(idx + q.length)}
        </>
    );
}

function VoucherRow({ v, onChange, highlight = "" }) {
    const [redeemOpen, setRedeemOpen] = useState(false);
    const [amount, setAmount] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    async function doRedeem() {
        const n = parseFloat(amount);
        if (!(n > 0)) {
            setErr("Enter an amount greater than 0");
            return;
        }
        setBusy(true);
        setErr("");
        try {
            await redeemVoucher(v.code, n);
            setAmount("");
            setRedeemOpen(false);
            onChange && onChange();
        } catch (e) {
            setErr(e.message || "Failed to redeem");
        } finally {
            setBusy(false);
        }
    }

    async function doVoid() {
        if (!window.confirm(`Void voucher ${v.code}? You can UNDO this if it was a mistake.`)) {
            return;
        }
        setBusy(true);
        try {
            await voidVoucher(v.code);
            onChange && onChange();
        } catch (e) {
            setErr(e.message || "Failed to void");
        } finally {
            setBusy(false);
        }
    }

    async function doUndo() {
        const hasReds = v.redemptions && v.redemptions.length > 0;
        const kind =
            v.status === "voided" ? "un-void this voucher" : "reverse the last redemption";
        if (!window.confirm(`${kind[0].toUpperCase() + kind.slice(1)}?`)) {
            return;
        }
        setBusy(true);
        setErr("");
        try {
            await undoVoucher(v.code);
            onChange && onChange();
        } catch (e) {
            setErr(e.message || "Nothing to undo");
        } finally {
            setBusy(false);
        }
    }

    const statusColor = {
        active: "text-[#3FA862]",
        used: "text-[#FAF8F4]/40",
        voided: "text-[#EB4A18]",
        expired: "text-[#FAF8F4]/30",
    }[v.status] || "text-[#FAF8F4]/60";

    // Format expiry date "28 Jul 2029"
    let expiryText = "";
    if (v.expires_at) {
        try {
            expiryText = new Date(v.expires_at).toLocaleDateString("en-AU", {
                day: "numeric",
                month: "short",
                year: "numeric",
            });
        } catch { /* ignore */ }
    }

    const canUndo =
        v.status === "voided" ||
        (v.redemptions && v.redemptions.length > 0);

    return (
        <article
            data-testid={`voucher-row-${v.code}`}
            className="border border-[#FAF8F4]/10 p-5 md:p-6"
        >
            <div className="grid md:grid-cols-[1fr_1fr_1fr_auto] gap-4 md:gap-6 items-center">
                {/* Code + status */}
                <div>
                    <p className="font-mono text-lg tracking-wider text-[#FAF8F4]">
                        {highlightMatch(v.code, highlight)}
                    </p>
                    <p
                        className={`mt-1 text-[0.6rem] uppercase tracking-[0.22em] font-bold ${statusColor}`}
                    >
                        {v.status}
                    </p>
                </div>

                {/* Buyer / recipient */}
                <div>
                    <p className="text-sm text-[#FAF8F4]/90">
                        {highlightMatch(v.buyer_name || "—", highlight)}
                    </p>
                    <p className="text-[0.65rem] uppercase tracking-[0.22em] text-[#FAF8F4]/40 mt-1">
                        {highlightMatch(v.buyer_email, highlight)}
                    </p>
                    {v.is_gift && v.recipient_name && (
                        <p className="text-[0.6rem] uppercase tracking-[0.18em] text-[#EB4A18]/80 mt-1">
                            Gift → {highlightMatch(v.recipient_name, highlight)}
                        </p>
                    )}
                </div>

                {/* Balance */}
                <div>
                    <p className="overline !text-[#FAF8F4]/40 text-[0.55rem]">
                        Remaining
                    </p>
                    <p className="font-display text-3xl text-[#FAF8F4] leading-none">
                        ${(v.amount_remaining || 0).toFixed(2)}
                    </p>
                    <p className="text-[0.6rem] uppercase tracking-[0.22em] text-[#FAF8F4]/40 mt-1">
                        of ${(v.amount_initial || 0).toFixed(2)}
                    </p>
                    {expiryText && (
                        <p className="text-[0.55rem] uppercase tracking-[0.22em] text-[#FAF8F4]/30 mt-2">
                            Expires {expiryText}
                        </p>
                    )}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2 min-w-[140px]">
                    {v.status === "active" && (
                        <button
                            disabled={busy}
                            data-testid={`voucher-redeem-btn-${v.code}`}
                            onClick={() => setRedeemOpen((o) => !o)}
                            className="bg-[#EB4A18] hover:bg-[#FAF8F4] hover:text-[#121212] text-[#FAF8F4] px-4 py-2.5 text-[0.65rem] uppercase tracking-[0.22em] font-bold transition-colors duration-200 disabled:opacity-50"
                        >
                            {redeemOpen ? "Cancel" : "Redeem"}
                        </button>
                    )}
                    {canUndo && (
                        <button
                            disabled={busy}
                            data-testid={`voucher-undo-btn-${v.code}`}
                            onClick={doUndo}
                            className="bg-transparent border border-[#FAF8F4]/25 text-[#FAF8F4]/80 hover:border-[#3FA862] hover:text-[#3FA862] px-4 py-2 text-[0.6rem] uppercase tracking-[0.22em] font-bold transition-colors duration-200 disabled:opacity-50"
                            title={
                                v.status === "voided"
                                    ? "Un-void this voucher"
                                    : "Reverse the last redemption"
                            }
                        >
                            ↺ Undo
                        </button>
                    )}
                    {v.status === "active" && (
                        <button
                            disabled={busy}
                            data-testid={`voucher-void-btn-${v.code}`}
                            onClick={doVoid}
                            className="bg-transparent text-[#FAF8F4]/40 hover:text-[#EB4A18] px-4 py-1 text-[0.6rem] uppercase tracking-[0.22em] font-bold transition-colors duration-200"
                        >
                            Void ×
                        </button>
                    )}
                    {err && (
                        <p className="text-[0.6rem] text-[#EB4A18] mt-1">{err}</p>
                    )}
                </div>
            </div>

            {/* Redeem form */}
            {redeemOpen && (
                <div className="mt-4 pt-4 border-t border-[#FAF8F4]/10 flex flex-col md:flex-row gap-3 items-start md:items-center">
                    <label className="text-xs uppercase tracking-[0.18em] text-[#FAF8F4]/60">
                        Deduct amount:
                    </label>
                    <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={v.amount_remaining}
                        data-testid={`voucher-redeem-amount-${v.code}`}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-32 p-2.5 bg-[#0B0A09] border border-[#FAF8F4]/20 focus:border-[#EB4A18] outline-none text-[#FAF8F4] text-sm"
                    />
                    <button
                        disabled={busy}
                        data-testid={`voucher-redeem-confirm-${v.code}`}
                        onClick={doRedeem}
                        className="bg-[#EB4A18] hover:bg-[#FAF8F4] hover:text-[#121212] text-[#FAF8F4] px-5 py-2.5 text-[0.65rem] uppercase tracking-[0.22em] font-bold transition-colors duration-200 disabled:opacity-50"
                    >
                        Deduct
                    </button>
                    {err && (
                        <p className="text-xs text-[#EB4A18] ml-2">{err}</p>
                    )}
                </div>
            )}

            {/* Redemption history */}
            {v.redemptions && v.redemptions.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[#FAF8F4]/5">
                    <p className="overline !text-[#FAF8F4]/40 text-[0.55rem] mb-2">
                        Redemption history
                    </p>
                    <ul className="space-y-1">
                        {v.redemptions.map((r, i) => (
                            <li
                                key={i}
                                className="text-[0.65rem] uppercase tracking-[0.16em] text-[#FAF8F4]/50 flex justify-between"
                            >
                                <span>
                                    -${r.amount.toFixed(2)}
                                </span>
                                <span>
                                    {new Date(r.at).toLocaleString("en-AU", {
                                        day: "numeric",
                                        month: "short",
                                        hour: "numeric",
                                        minute: "2-digit",
                                    })}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </article>
    );
}


// ---------------------------------------------------------------------------
// Voucher redemption help modal
// ---------------------------------------------------------------------------
function VoucherHelpModal({ onClose }) {
    useEffect(() => {
        function onKey(e) {
            if (e.key === "Escape") onClose();
        }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    return (
        <div
            data-testid="vouchers-help-modal"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative bg-[#121212] border border-[#FAF8F4]/10 max-w-lg w-full p-8 max-h-[85vh] overflow-y-auto"
            >
                <button
                    type="button"
                    data-testid="vouchers-help-close"
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center text-[#FAF8F4]/60 hover:text-[#EB4A18] text-2xl leading-none"
                >
                    ×
                </button>
                <p className="overline !text-[#EB4A18] text-[0.6rem]">
                    Help · Redeeming a voucher
                </p>
                <h3 className="mt-2 font-display uppercase text-2xl md:text-3xl leading-tight text-[#FAF8F4]">
                    How to redeem<span className="text-[#EB4A18]">.</span>
                </h3>
                <ol className="mt-6 space-y-5 text-sm text-[#FAF8F4]/85 leading-relaxed">
                    <li className="flex gap-4">
                        <span className="font-display text-[#EB4A18] text-2xl leading-none w-6 flex-shrink-0">
                            1
                        </span>
                        <span>
                            <strong className="text-[#FAF8F4]">
                                Ask the customer for their voucher code.
                            </strong>{" "}
                            It looks like{" "}
                            <span className="font-mono text-[#EB4A18]">
                                GAF-9K2M-XT4P
                            </span>{" "}
                            — they&apos;ll have it in an email on their phone.
                        </span>
                    </li>
                    <li className="flex gap-4">
                        <span className="font-display text-[#EB4A18] text-2xl leading-none w-6 flex-shrink-0">
                            2
                        </span>
                        <span>
                            <strong className="text-[#FAF8F4]">
                                Find their voucher in the list.
                            </strong>{" "}
                            Use the search bar above — type either the{" "}
                            <span className="text-[#EB4A18] font-bold">code</span>{" "}
                            (e.g.{" "}
                            <span className="font-mono text-[#EB4A18]">
                                GAF-9K2M
                            </span>
                            ) or the{" "}
                            <span className="text-[#EB4A18] font-bold">
                                customer&apos;s name
                            </span>{" "}
                            (buyer&apos;s or gift recipient&apos;s). The row filters
                            instantly. Make sure you&apos;re on the{" "}
                            <span className="text-[#3FA862] font-bold">ACTIVE</span>{" "}
                            tab.
                        </span>
                    </li>
                    <li className="flex gap-4">
                        <span className="font-display text-[#EB4A18] text-2xl leading-none w-6 flex-shrink-0">
                            3
                        </span>
                        <span>
                            <strong className="text-[#FAF8F4]">Tap REDEEM.</strong>{" "}
                            A &quot;Deduct amount&quot; field appears.
                        </span>
                    </li>
                    <li className="flex gap-4">
                        <span className="font-display text-[#EB4A18] text-2xl leading-none w-6 flex-shrink-0">
                            4
                        </span>
                        <span>
                            <strong className="text-[#FAF8F4]">
                                Type the amount of their order.
                            </strong>{" "}
                            E.g. their meal is $18.50 → enter{" "}
                            <span className="font-mono text-[#EB4A18]">18.50</span>{" "}
                            → tap DEDUCT.
                        </span>
                    </li>
                    <li className="flex gap-4">
                        <span className="font-display text-[#EB4A18] text-2xl leading-none w-6 flex-shrink-0">
                            5
                        </span>
                        <span>
                            <strong className="text-[#FAF8F4]">Done.</strong>{" "}
                            Balance updates instantly. Any leftover stays on the
                            voucher for next time. When it hits $0 it auto-marks
                            as USED and disappears from Active.
                        </span>
                    </li>
                </ol>
                <div className="mt-8 p-4 bg-[#EB4A18]/10 border border-[#EB4A18]/30">
                    <p className="text-[0.7rem] uppercase tracking-[0.18em] font-bold text-[#EB4A18] mb-2">
                        Made a mistake?
                    </p>
                    <p className="text-sm text-[#FAF8F4]/85 leading-relaxed mb-2">
                        Every action can be reversed. Tap the{" "}
                        <span className="font-bold text-[#3FA862]">↺ UNDO</span>{" "}
                        button on the row to:
                    </p>
                    <ul className="text-sm text-[#FAF8F4]/85 space-y-1.5 leading-relaxed">
                        <li>
                            • <strong>Reverse the last redemption</strong> — the
                            amount goes straight back onto the voucher balance
                        </li>
                        <li>
                            • <strong>Un-void an accidentally voided voucher</strong>{" "}
                            — brings it back to active
                        </li>
                    </ul>
                </div>
                <div className="mt-4 p-4 bg-black/40 border border-[#FAF8F4]/10">
                    <p className="text-[0.7rem] uppercase tracking-[0.18em] font-bold text-[#FAF8F4]/70 mb-2">
                        Rules
                    </p>
                    <ul className="text-sm text-[#FAF8F4]/80 space-y-1.5 leading-relaxed">
                        <li>• No cash back — no refunds on unused balance</li>
                        <li>• Voucher can be split across multiple visits</li>
                        <li>
                            • Can&apos;t redeem more than the remaining balance
                            — system won&apos;t let you
                        </li>
                        <li>
                            • Vouchers expire <strong>3 years</strong> from
                            purchase (auto-marked as EXPIRED)
                        </li>
                    </ul>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="mt-8 w-full bg-[#EB4A18] hover:bg-[#FAF8F4] hover:text-[#121212] text-[#FAF8F4] px-5 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors duration-200"
                >
                    Got it
                </button>
            </div>
        </div>
    );
}

